import { Component, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ColDef, GridApi, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { MenuStateService } from '../menu-state.service';
import { ShipmentTripLists } from '../models/shipment-trip-lists.model';
import { ShipmentListsModalComponent } from '../shipment-lists/shipment-lists-modal/shipment-lists-modal.component';
import { BlindUploadModalComponent } from '../trip-lists/blind-upload-modal/blind-upload-modal.component';
import { TripPackageOperationsDetailComponent } from './trip-package-operations-detail.component';

type ThemeMode = 'dark' | 'light';

@Component({
  selector: 'app-trip-package-operations',
  templateUrl: './trip-package-operations.component.html',
  styleUrls: ['./trip-package-operations.component.scss']
})
export class TripPackageOperationsComponent {
  public detailCellRenderer: any = TripPackageOperationsDetailComponent;
  themeMode: ThemeMode = 'light';
  totalRows = 0;
  loadedRows = 0;
  loadingRows = false;
  errorMessage = '';
  lastLoadedAt: Date | null = null;

  private gridApi!: GridApi;
  private readonly themeStorageKey = 'trip-package-operations-theme';
  private readonly customerNameCache = new Map<string, string>();
  private readonly pendingCustomerLoads = new Set<string>();

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellClass: 'cell-wrap-text',
    minWidth: 120
  };

  rowClassRules = {
    'tpo-row--blind': (params: any) => params.data?.blind_scan === true && !params.data?.metadata_uploaded
  };

  columnDefs: ColDef[] = [
    { field: 'trip_list_uuid', headerName: 'UUID', hide: true },
    {
      field: 'asn_number',
      headerName: 'ASN No.',
      cellRenderer: 'agGroupCellRenderer',
      filter: 'agTextColumnFilter',
      width: 170,
      pinned: 'left',
      cellRendererParams: {
        innerRenderer: (params: any) => this.identifierCell(params.value || 'No ASN', params.data?.blind_scan ? 'Blind intake' : '')
      }
    },
    {
      field: 'trip_number',
      headerName: 'Trip No.',
      filter: 'agNumberColumnFilter',
      width: 150,
      cellRenderer: (params: any) => this.tripNumberCell(params)
    },
    {
      field: 'customer_display_name',
      headerName: 'Customer',
      filter: 'agTextColumnFilter',
      width: 245,
      cellRenderer: (params: any) => this.customerCell(params)
    },
    { field: 'date', headerName: 'Date', width: 150, cellRenderer: (params: any) => this.dateCell(params.value) },
    { field: 'week_no', headerName: 'Week No.', width: 105, cellRenderer: (params: any) => this.pillCell(params.value, 'fa-calendar-week', '') },
    { field: 'external_bill_of_lading', headerName: 'Bill Of Lading No.', width: 175, cellRenderer: (params: any) => this.referenceCell(params.value) },
    { field: 'act_trail_number', headerName: 'ACT Trailer No.', width: 150, cellRenderer: (params: any) => this.referenceCell(params.value) },
    { field: 'trailer_id', headerName: 'Trailer ID', width: 135, cellRenderer: (params: any) => this.referenceCell(params.value) },
    { field: 'total_cartons', headerName: 'Cartons', width: 110, cellRenderer: (params: any) => this.countCell(params.value) },
    { field: 'manifest_carton_count', headerName: 'Manifest', width: 120, cellRenderer: (params: any) => this.countCell(params.value) },
    { field: 'bol_carton_count', headerName: 'BOL Count', width: 120, cellRenderer: (params: any) => this.countCell(params.value) },
    { field: 'reported_weight', headerName: 'Weight', width: 125, cellRenderer: (params: any) => this.countCell(params.value, 'lbs') },
    { field: 'created', headerName: 'Created', width: 145, cellRenderer: (params: any) => this.dateCell(params.value, true) },
    { field: 'archive', headerName: 'Archive', width: 115, cellRenderer: (params: any) => this.archiveCell(params.value) },
    {
      headerName: 'Package Ops',
      cellRenderer: (params: any) => this.packageActionsRenderer(params),
      editable: false,
      pinned: 'right',
      width: 190,
      minWidth: 190,
      sortable: false,
      filter: false,
      suppressMovable: true
    }
  ];

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    private zone: NgZone,
    private menuStateService: MenuStateService
  ) {
    this.initializeTheme();
  }

  get themeClass(): string {
    return 'ag-theme-ulaap tpo-grid';
  }

  get statusLabel(): string {
    if (this.loadingRows) {
      return 'Loading Trips';
    }

    if (this.errorMessage) {
      return 'Grid Issue';
    }

    return this.totalRows ? 'Ready' : 'Waiting';
  }

  get statusTone(): 'ready' | 'warning' | 'error' | 'idle' {
    if (this.errorMessage) {
      return 'error';
    }

    if (this.loadingRows) {
      return 'warning';
    }

    return this.totalRows ? 'ready' : 'idle';
  }

  get statusClass(): string {
    return `tpo-status--${this.statusTone}`;
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    params.api.setGridOption('serverSideDatasource', this.getServerSideDatasource());
  }

  refreshGrid() {
    this.errorMessage = '';
    if (this.gridApi) {
      this.gridApi.refreshServerSide({ purge: true });
    }
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        this.loadingRows = true;
        this.errorMessage = '';

        this.getShipments(
          params.request.startRow,
          params.request.endRow,
          params.request.filterModel,
          params.request.sortModel
        ).subscribe(
          response => {
            this.loadingRows = false;
            if (response) {
              this.totalRows = response.rowCount;
              this.loadedRows = response.rowData.length;
              this.lastLoadedAt = new Date();
              params.success({
                rowData: response.rowData,
                rowCount: response.rowCount
              });
            } else {
              this.errorMessage = 'Trip list did not return rows.';
              params.fail();
            }
          },
          error => {
            console.log(error);
            this.loadingRows = false;
            this.errorMessage = 'Unable to load package operations trips.';
            params.fail();
          }
        );
      }
    };
  }

  getContextMenuItems = (params: any) => {
    const rowData = params.node?.data;
    const items: any[] = ['copy', 'copyWithHeaders', 'export', 'separator'];

    if (rowData) {
      items.unshift(
        {
          name: 'Open Package Labels',
          icon: '<i class="fas fa-tags"></i>',
          action: () => this.zone.run(() => this.openPackageLabels(rowData))
        },
        {
          name: 'Add Packages To Trip',
          icon: '<i class="fas fa-file-arrow-up"></i>',
          action: () => this.zone.run(() => this.addPackagesToTrip(rowData))
        },
        'separator'
      );
    }

    if (rowData?.blind_scan) {
      items.unshift({
        name: 'Upload Blind Data',
        icon: '<i class="fas fa-upload"></i>',
        action: () => this.zone.run(() => this.openBlindUploadModal(rowData))
      });
    }

    return items;
  };

  openPackageLabels(rowData: any) {
    const tripUuid = rowData?.trip_list_uuid;
    if (!tripUuid) return;

    this.router.navigate(['/trip_package_labels', tripUuid]);
  }

  addPackagesToTrip(rowData: any) {
    const tripUuid = rowData?.trip_list_uuid;
    if (!tripUuid) return;

    this.router.navigate(['/package_intake'], {
      queryParams: {
        mode: 'existing_trip',
        trip_list_uuid: tripUuid
      }
    });
  }

  openBlindUploadModal(rowData: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '900px';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.height = '80vh';
    dialogConfig.maxHeight = '90vh';
    dialogConfig.data = {
      modalTitle: `Upload Data for Trip ${rowData.trip_number}`,
      trip: rowData
    };

    const dialogRef = this.dialog.open(BlindUploadModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Blind upload payload:', result);
      }
    });
  }

  getShipments(startRow: number = 0, endRow: number = 0, filterModel: any = {}, sortModel: any = []) {
    const urlApi = '/api/shipment_lists/trip_lists.php';

    const filters = Object.entries(filterModel).map(([column, filter]: [string, any]) => {
      let operator = '';
      let value: any = [];

      if (filter.filterType === 'text') {
        operator = filter.type;
        value = filter.filter;
      } else if (filter.filterType === 'set') {
        operator = 'equals';
        value = filter.values;
      } else if (filter.filterType === 'date') {
        if (filter.type === 'inRange') {
          operator = filter.type;
          value = [filter.dateFrom, filter.dateTo];
        } else {
          operator = 'equals';
          value = filter.dateFrom;
        }
      } else if (filter.filterType === 'number') {
        if (filter.type === 'inRange') {
          operator = filter.type;
          value = [filter.filter, filter.filterTo];
        } else {
          operator = 'equals';
          value = filter.filter;
        }
      }

      return { column, operator, value };
    });

    const params: any = {
      startRow,
      endRow,
      sortModel: JSON.stringify(sortModel),
      filters: JSON.stringify(filters)
    };

    return this.http.get<any>(urlApi, { params }).pipe(
      map(response => {
        const rowData = (response.data ?? []).map((tripListsData: any) => {
          const tripLists = new ShipmentTripLists();
          Object.assign(tripLists, tripListsData);
          (tripLists as any).customer_display_name = this.resolveCustomerName(tripListsData);
          return tripLists;
        });

        this.loadMissingCustomerNames(rowData);
        return { rowData, rowCount: response.totalRows ?? rowData.length };
      }),
      catchError(error => {
        console.log(error);
        this.errorMessage = 'Unable to load package operations trips.';
        return of(null);
      }),
      filter(response => response !== null)
    );
  }

  packageIntake() {
    this.router.navigate(['/package_intake']);
  }

  storeReports() {
    this.router.navigate(['/store_reports']);
  }

  exceptionReports() {
    this.router.navigate(['/exception_reports']);
  }

  deliveryReports() {
    this.router.navigate(['/delivery_reports']);
  }

  uploadYoshinoFile() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '530px';
    dialogConfig.height = '200px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Upload Yoshino File',
        modalMessage: ''
      }
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  toggleTheme() {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  formatLastLoaded(): string {
    return this.lastLoadedAt ? this.lastLoadedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Not loaded';
  }

  private customerCell(params: any): HTMLElement {
    const wrap = document.createElement('div');
    wrap.classList.add('tpo-customer-cell');

    const name = this.resolveCustomerName(params.data);
    const strong = document.createElement('strong');
    strong.textContent = name || 'Customer lookup';
    if (!name) wrap.classList.add('is-loading');

    wrap.appendChild(strong);
    return wrap;
  }

  private resolveCustomerName(rowData: any): string {
    if (!rowData) return '';

    const directName = this.firstDisplayValue(
      rowData.customer_display_name,
      rowData.contact_organization,
      rowData.customer_name,
      rowData.customer_contact_organization,
      rowData.inventory_warehouse_customer_name,
      rowData.customer_contact_name,
      rowData.trip_list_customer_contact_organization
    );

    if (directName) return directName;

    const uuid = this.customerUuid(rowData);
    return uuid ? this.customerNameCache.get(uuid) || '' : '';
  }

  private loadMissingCustomerNames(rowData: any[]): void {
    const uuids = new Set<string>();
    for (const row of rowData) {
      const uuid = this.customerUuid(row);
      if (uuid && !this.customerNameCache.has(uuid) && !this.pendingCustomerLoads.has(uuid)) {
        uuids.add(uuid);
      }
    }

    uuids.forEach(uuid => {
      this.pendingCustomerLoads.add(uuid);
      this.http.get<any>('/api/contacts/contact_details.php', { params: { uuid } }).pipe(
        catchError(error => {
          console.log(error);
          return of(null);
        })
      ).subscribe(response => {
        this.pendingCustomerLoads.delete(uuid);
        const name = this.extractContactName(response);
        if (!name) return;

        this.customerNameCache.set(uuid, name);
        if (!this.gridApi) return;

        this.gridApi.forEachNode(node => {
          if (this.customerUuid(node.data) === uuid) {
            node.data.customer_display_name = name;
          }
        });
        this.gridApi.refreshCells({ force: true, columns: ['customer_display_name'] });
      });
    });
  }

  private customerUuid(rowData: any): string {
    return this.firstDisplayValue(
      rowData?.customer_contact_uuid,
      rowData?.trip_list_customer_contact_uuid,
      rowData?.inventory_warehouse_customer_contact_uuid,
      rowData?.customer_uuid
    );
  }

  private extractContactName(response: any): string {
    return this.firstDisplayValue(
      response?.contact_organization,
      response?.customer_name,
      response?.data?.contact_organization,
      response?.data?.customer_name,
      response?.data?.contact?.contact_organization,
      response?.contact?.contact_organization,
      Array.isArray(response?.data) ? response.data[0]?.contact_organization : ''
    );
  }

  private identifierCell(value: any, label = ''): HTMLElement {
    const wrap = document.createElement('div');
    wrap.classList.add('tpo-identifier-cell');

    const strong = document.createElement('strong');
    strong.textContent = this.displayValue(value);
    wrap.appendChild(strong);

    if (label) {
      const small = document.createElement('span');
      small.textContent = label;
      wrap.appendChild(small);
    }

    return wrap;
  }

  private tripNumberCell(params: any): HTMLElement {
    const wrap = this.identifierCell(params.value || 'No trip');
    if (params.data?.blind_scan === true && !params.data?.metadata_uploaded) {
      const icon = document.createElement('i');
      icon.classList.add('fas', 'fa-eye-slash');
      icon.title = 'Blind trip - requires metadata upload';
      wrap.appendChild(icon);
    }
    return wrap;
  }

  private dateCell(value: any, compact = false): HTMLElement {
    const wrap = document.createElement('div');
    wrap.classList.add('tpo-date-cell');

    const date = value ? new Date(value) : null;
    const isValid = date && !Number.isNaN(date.getTime());
    const main = document.createElement('strong');
    const sub = document.createElement('span');

    if (isValid && date) {
      main.textContent = compact ? date.toLocaleDateString([], { month: 'short', day: 'numeric' }) : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
      sub.textContent = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      wrap.appendChild(main);
      wrap.appendChild(sub);
      return wrap;
    }

    main.textContent = this.displayValue(value);
    wrap.appendChild(main);
    return wrap;
  }

  private pillCell(value: any, iconClass: string, label: string): HTMLElement {
    const wrap = document.createElement('span');
    wrap.classList.add('tpo-pill-cell');

    const icon = document.createElement('i');
    icon.classList.add('fa-solid', iconClass);

    const text = document.createElement('span');
    const empty = value === null || value === undefined || value === '';
    text.textContent = empty ? '-' : label ? `${label} ${value}` : String(value);

    wrap.appendChild(icon);
    wrap.appendChild(text);
    if (empty) wrap.classList.add('is-empty');
    return wrap;
  }

  private referenceCell(value: any): HTMLElement {
    const wrap = document.createElement('div');
    wrap.classList.add('tpo-reference-cell');

    const strong = document.createElement('strong');
    strong.textContent = this.displayValue(value);
    if (strong.textContent === '-') wrap.classList.add('is-empty');

    wrap.appendChild(strong);
    return wrap;
  }

  private countCell(value: any, suffix = ''): HTMLElement {
    const wrap = document.createElement('div');
    wrap.classList.add('tpo-count-cell');

    const strong = document.createElement('strong');
    strong.textContent = this.displayValue(value);
    if (suffix && strong.textContent !== '-') strong.textContent += ` ${suffix}`;

    wrap.appendChild(strong);
    if (strong.textContent === '-') wrap.classList.add('is-empty');
    return wrap;
  }

  private archiveCell(value: any): HTMLElement {
    const archived = value === true || value === 'true' || value === 1 || value === '1';
    const wrap = this.pillCell(archived ? 'Archived' : 'Active', archived ? 'fa-box-archive' : 'fa-box-open', '');
    wrap.classList.add(archived ? 'is-archived' : 'is-active');
    return wrap;
  }

  private displayValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  private firstDisplayValue(...values: any[]): string {
    for (const value of values) {
      if (value !== null && value !== undefined && value !== '') return String(value);
    }
    return '';
  }
  private packageActionsRenderer(params: any): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.classList.add('package-op-actions');

    const labelsButton = this.packageActionButton('Labels', 'fas fa-tags', 'Open package labels', () => this.openPackageLabels(params.data));
    const addButton = this.packageActionButton('Add', 'fas fa-file-arrow-up', 'Add packages to this trip', () => this.addPackagesToTrip(params.data));

    wrapper.appendChild(labelsButton);
    wrapper.appendChild(addButton);
    return wrapper;
  }

  private packageActionButton(label: string, iconClass: string, title: string, action: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = title;
    button.classList.add('package-op-button');
    button.innerHTML = `<i class="${iconClass}"></i><span>${label}</span>`;
    button.addEventListener('click', () => this.zone.run(action));
    return button;
  }

  private initializeTheme(): void {
    const savedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (savedTheme === 'dark' || savedTheme === 'light') {
      this.themeMode = savedTheme;
      return;
    }

    const currentTheme = this.menuStateService.themeState$.getValue();
    this.themeMode = currentTheme === 'ams-content-dark' ? 'dark' : 'light';
  }
}
